import * as store from "../state/store.js";
import type { LitElement } from "lit";
import type { SelectionMode } from "../state/types.js";
import { entityAtScreenPos, entitiesInBox } from "./hit-test.js";
import { panBy, zoomAt } from "../ops/view.js";
import {
  addToSelection,
  deselectAll,
  replaceSelection,
  selectAll,
  toggleInSelection,
  toggleIntoSelection,
  unionIntoSelection,
} from "../ops/entities.js";
import { undo, redo } from "../ops/history.js";
import { closeContextMenu, openContextMenu, openModal, toggleSidebar } from "../ops/ui.js";
import { openFilePicker, loadDxfFromFile, resetView } from "../ops/io.js";

// Distance in pixels the pointer must move before a click-select gesture is
// promoted to a drag-select. Small enough that a stable click still registers;
// large enough that a few pixels of jitter (or the head of a fingertip on
// touch) doesn't accidentally start a drag.
const DRAG_THRESHOLD_PX = 3;
// Touch drag needs a slightly larger threshold — fingertips have real
// contact area and a "stationary" tap can drift ~5 px in practice.
const TOUCH_DRAG_THRESHOLD_PX = 8;
// Long-press duration to open the context menu on touch. Matches Android /
// iOS system-level long-press so it feels native.
const LONG_PRESS_MS = 500;
// If the pointer moves more than this while the long-press timer is running,
// the gesture is reinterpreted as a pan instead of a menu request.
const LONG_PRESS_MOVE_TOLERANCE_PX = 10;
// Hit-test tolerance for touch tap-to-select. A fingertip has a real contact
// area (~10-15px effective aim precision); the mouse-tuned 5px threshold in
// hit-test.ts routinely misses thin lines under a fingertip. 14px feels
// forgiving without the ambiguity that comes from very large radii.
const TOUCH_HIT_TOLERANCE_PX = 14;

type PointerKind = "mouse" | "touch" | "pen";
type ActivePointer = {
  id: number;
  kind: PointerKind;
  downX: number;
  downY: number;
  x: number;
  y: number;
  downTime: number;
  moved: boolean;
  button: number;
};

// Attach every input source (pointer on canvas + keyboard on document) needed
// to drive the editor. Returns a teardown function.
export function attachInput(host: LitElement, canvas: HTMLCanvasElement): () => void {
  const state = {
    isPanning: false,
    isSelecting: false,
    dragStartX: 0,
    dragStartY: 0,
    lastX: 0,
    lastY: 0,
    selectMode: "inside" as SelectionMode,
    rightClickStart: null as { x: number; y: number } | null,
    rightClickTimer: 0,
    isRightClickPan: false,
    // A left-click gesture that hasn't yet committed to "click" or "drag".
    // Set on left pointerdown, cleared on pointermove past the drag threshold
    // (promotes to drag-select) or on pointerup (commits as single-click).
    pendingClick: null as
      | {
          sx: number;
          sy: number;
          hit: { id: number } | null;
          shiftKey: boolean;
          ctrlKey: boolean;
        }
      | null,
    // Touch-specific state.
    // pointers: every active touch/pen pointer, keyed by pointerId.
    pointers: new Map<number, ActivePointer>(),
    // longPress: the queued context-menu opener when the user is holding a
    // single finger still. Cancelled when a second touch lands or when the
    // finger moves past the tolerance.
    longPressTimer: 0,
    longPressAt: null as { x: number; y: number } | null,
    // Pinch: reference distance + midpoint captured when a second finger
    // lands. Every subsequent move computes deltas against these snapshots
    // so the zoom feels continuous instead of jumping between reference
    // frames on each pointermove.
    pinch: null as
      | {
          startDist: number;
          startMidX: number;
          startMidY: number;
          lastDist: number;
          lastMidX: number;
          lastMidY: number;
        }
      | null,
    // Two-finger tap → box-select mode toggle. If both fingers land within
    // a short window without moving, treat the next single-finger drag as
    // a rectangle select instead of a pan. (Set on second-finger-down;
    // consumed on the next single-finger pointerdown.)
    boxSelectArmed: false,
  };

  const selectionBoxEl = host.renderRoot.querySelector<HTMLDivElement>("#selection-box");
  const modeBadgeEl = host.renderRoot.querySelector<HTMLDivElement>("#mode-badge");
  const modeNameEl = host.renderRoot.querySelector<HTMLElement>("#mode-badge-name");

  const setBox = (x: number, y: number, w: number, h: number, mode: SelectionMode): void => {
    if (!selectionBoxEl) return;
    selectionBoxEl.style.left = `${x}px`;
    selectionBoxEl.style.top = `${y}px`;
    selectionBoxEl.style.width = `${w}px`;
    selectionBoxEl.style.height = `${h}px`;
    selectionBoxEl.classList.toggle("outside", mode === "outside");
    selectionBoxEl.classList.add("visible");
  };

  const hideBox = (): void => {
    selectionBoxEl?.classList.remove("visible");
    modeBadgeEl?.classList.remove("visible");
  };

  const showMode = (mode: SelectionMode): void => {
    if (!modeBadgeEl || !modeNameEl) return;
    const names = { inside: "Inside", cross: "Cross", outside: "Outside" };
    modeNameEl.textContent = names[mode];
    modeBadgeEl.classList.add("visible");
  };

  const canvasLocal = (e: { clientX: number; clientY: number }) => {
    const canvasRect = canvas.getBoundingClientRect();
    return { sx: e.clientX - canvasRect.left, sy: e.clientY - canvasRect.top };
  };

  const cancelLongPress = () => {
    if (state.longPressTimer) {
      clearTimeout(state.longPressTimer);
      state.longPressTimer = 0;
    }
    state.longPressAt = null;
  };

  // ---------------- MOUSE PATH ----------------
  //
  // Preserves the original semantics 1:1 so desktop workflow doesn't shift:
  //   left-drag  → drag-select (any modifier semantics), tap = pick
  //   middle     → pan
  //   right      → pan on drag / context menu on click
  //   wheel      → zoom to cursor

  const onMousePointerDown = (e: PointerEvent) => {
    const local = canvasLocal(e);
    closeContextMenu();

    if (e.button === 1) {
      state.isPanning = true;
      state.lastX = e.clientX;
      state.lastY = e.clientY;
      canvas.style.cursor = "grabbing";
      canvas.setPointerCapture(e.pointerId);
      e.preventDefault();
      return;
    }

    if (e.button === 2) {
      e.preventDefault();
      state.rightClickStart = { x: e.clientX, y: e.clientY };
      state.isRightClickPan = false;
      state.rightClickTimer = window.setTimeout(() => {
        if (!state.isRightClickPan) openContextMenu(e.clientX, e.clientY);
        state.rightClickTimer = 0;
      }, 250);
      return;
    }

    if (e.button === 0) {
      const hit = entityAtScreenPos(hitInput(canvas), local.sx, local.sy);
      state.pendingClick = {
        sx: local.sx,
        sy: local.sy,
        hit,
        shiftKey: e.shiftKey,
        ctrlKey: e.ctrlKey || e.metaKey,
      };
      canvas.setPointerCapture(e.pointerId);
      e.preventDefault();
    }
  };

  const onMousePointerMove = (e: PointerEvent) => {
    const local = canvasLocal(e);

    if (state.rightClickStart) {
      const dx = e.clientX - state.rightClickStart.x;
      const dy = e.clientY - state.rightClickStart.y;
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        state.isRightClickPan = true;
        if (state.rightClickTimer) {
          clearTimeout(state.rightClickTimer);
          state.rightClickTimer = 0;
        }
        if (!state.isPanning) {
          state.isPanning = true;
          state.lastX = e.clientX;
          state.lastY = e.clientY;
          canvas.style.cursor = "grabbing";
        }
      }
    }

    if (state.isPanning) {
      const dx = e.clientX - state.lastX;
      const dy = e.clientY - state.lastY;
      state.lastX = e.clientX;
      state.lastY = e.clientY;
      panBy(dx, dy);
      return;
    }

    if (state.pendingClick) {
      const dx = local.sx - state.pendingClick.sx;
      const dy = local.sy - state.pendingClick.sy;
      if (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX) {
        const pending = state.pendingClick;
        state.pendingClick = null;
        state.isSelecting = true;
        state.dragStartX = pending.sx;
        state.dragStartY = pending.sy;
        if (!pending.shiftKey && !pending.ctrlKey) deselectAll();
      }
    }

    if (state.isSelecting) {
      const x = Math.min(state.dragStartX, local.sx);
      const y = Math.min(state.dragStartY, local.sy);
      const w = Math.abs(local.sx - state.dragStartX);
      const h = Math.abs(local.sy - state.dragStartY);
      const isLeftToRight = local.sx > state.dragStartX;
      let mode: SelectionMode = isLeftToRight ? "inside" : "cross";
      if (e.shiftKey && e.ctrlKey) mode = "outside";
      state.selectMode = mode;
      setBox(x, y, w, h, mode);
      showMode(mode);
      canvas.style.cursor = "crosshair";
      return;
    }

    canvas.style.cursor = "crosshair";
  };

  const onMousePointerUp = (e: PointerEvent) => {
    if (canvas.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }
    if (e.button === 2) {
      if (state.rightClickTimer) {
        clearTimeout(state.rightClickTimer);
        state.rightClickTimer = 0;
      }
      if (!state.isRightClickPan && state.rightClickStart) {
        openContextMenu(e.clientX, e.clientY);
      }
      state.rightClickStart = null;
      state.isRightClickPan = false;
      state.isPanning = false;
      canvas.style.cursor = "crosshair";
      e.preventDefault();
      return;
    }

    if (state.isPanning) {
      state.isPanning = false;
      canvas.style.cursor = "crosshair";
      return;
    }

    if (e.button === 0 && state.pendingClick) {
      const pending = state.pendingClick;
      state.pendingClick = null;
      commitPickSelection(pending);
      canvas.style.cursor = "crosshair";
      e.preventDefault();
      return;
    }

    if (state.isSelecting) {
      const local = canvasLocal(e);
      commitBoxSelection(local, e.ctrlKey || e.metaKey, e.shiftKey);
      state.isSelecting = false;
      hideBox();
      canvas.style.cursor = "crosshair";
      e.preventDefault();
    }
  };

  const commitPickSelection = (pending: {
    hit: { id: number } | null;
    shiftKey: boolean;
    ctrlKey: boolean;
  }) => {
    if (pending.hit) {
      const hitId = pending.hit.id;
      if (pending.shiftKey) addToSelection(hitId);
      else if (pending.ctrlKey) toggleInSelection(hitId);
      else {
        const sel = store.selectedEntityIds();
        if (sel.has(hitId) && sel.size === 1) replaceSelection([]);
        else replaceSelection([hitId]);
      }
    } else if (!pending.shiftKey && !pending.ctrlKey) {
      deselectAll();
    }
  };

  const commitBoxSelection = (
    local: { sx: number; sy: number },
    ctrl: boolean,
    shift: boolean
  ) => {
    const w = Math.abs(local.sx - state.dragStartX);
    const h = Math.abs(local.sy - state.dragStartY);
    if (w > 5 && h > 5) {
      const found = entitiesInBox(
        hitInput(canvas),
        state.dragStartX,
        state.dragStartY,
        local.sx,
        local.sy,
        state.selectMode
      );
      const ids = found.map((entity) => entity.id);
      if (ctrl) toggleIntoSelection(ids);
      else if (shift) unionIntoSelection(ids);
      else replaceSelection(ids);
    }
  };

  // ---------------- TOUCH / PEN PATH ----------------
  //
  // Gesture model:
  //   1-finger tap (no drag past threshold, no long-press):
  //                → pick / toggle the entity underneath (like a click)
  //   1-finger long-press (500 ms, no drag past ~10 px):
  //                → open the canvas context menu at that position
  //   1-finger drag (default):
  //                → pan the view. This is the natural CAD-mobile gesture;
  //                  pinch handles zoom so pan doesn't need to fight it.
  //   2-finger pinch:
  //                → zoom around the midpoint, plus pan by the midpoint delta
  //   2-finger tap (both down and up under move threshold, quick):
  //                → arm rectangle-select for the next 1-finger drag.
  //                  Tapping twice gets you what right-drag does on desktop.

  const onTouchPointerDown = (e: PointerEvent) => {
    const local = canvasLocal(e);
    closeContextMenu();

    const p: ActivePointer = {
      id: e.pointerId,
      kind: (e.pointerType as PointerKind) || "touch",
      downX: e.clientX,
      downY: e.clientY,
      x: e.clientX,
      y: e.clientY,
      downTime: performance.now(),
      moved: false,
      button: e.button,
    };
    state.pointers.set(e.pointerId, p);
    canvas.setPointerCapture(e.pointerId);
    e.preventDefault();

    // Second finger down → cancel pending single-finger gestures and enter
    // pinch mode. If we were mid-pan on the first finger, that pan ends
    // cleanly and the two-finger transform takes over.
    if (state.pointers.size === 2) {
      cancelLongPress();
      state.pendingClick = null;
      state.isPanning = false;
      state.isSelecting = false;
      hideBox();
      const [a, b] = Array.from(state.pointers.values());
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const dist = Math.hypot(dx, dy) || 1;
      const midX = (a.x + b.x) / 2;
      const midY = (a.y + b.y) / 2;
      state.pinch = {
        startDist: dist,
        startMidX: midX,
        startMidY: midY,
        lastDist: dist,
        lastMidX: midX,
        lastMidY: midY,
      };
      return;
    }

    // First finger down — either a tap, long-press, drag-pan, or (if the
    // previous two-finger tap armed box-select) start a rectangle drag.
    if (state.pointers.size === 1) {
      if (state.boxSelectArmed) {
        state.boxSelectArmed = false;
        state.isSelecting = true;
        state.dragStartX = local.sx;
        state.dragStartY = local.sy;
        // Box-select on touch: initial box is empty; drag inflates it.
        // Modifier keys aren't available on touch, so we always clear the
        // existing selection first (matches the "no modifier = replace"
        // desktop rule).
        deselectAll();
        setBox(local.sx, local.sy, 0, 0, "inside");
        showMode("inside");
        return;
      }
      const hit = entityAtScreenPos(
        hitInput(canvas),
        local.sx,
        local.sy,
        TOUCH_HIT_TOLERANCE_PX
      );
      state.pendingClick = {
        sx: local.sx,
        sy: local.sy,
        hit,
        shiftKey: false,
        ctrlKey: false,
      };
      state.lastX = e.clientX;
      state.lastY = e.clientY;
      state.longPressAt = { x: e.clientX, y: e.clientY };
      state.longPressTimer = window.setTimeout(() => {
        state.longPressTimer = 0;
        // Only fire if we're still holding one stationary finger. If the
        // user has already started dragging (pendingClick cleared) or
        // touched a second finger, the timer is a no-op.
        if (
          state.pointers.size === 1 &&
          state.pendingClick &&
          !state.isPanning &&
          !state.isSelecting
        ) {
          const pos = state.longPressAt;
          state.pendingClick = null;
          if (pos) openContextMenu(pos.x, pos.y);
        }
      }, LONG_PRESS_MS);
    }
  };

  const onTouchPointerMove = (e: PointerEvent) => {
    const p = state.pointers.get(e.pointerId);
    if (!p) return;
    p.x = e.clientX;
    p.y = e.clientY;
    const dx = e.clientX - p.downX;
    const dy = e.clientY - p.downY;
    if (Math.hypot(dx, dy) > LONG_PRESS_MOVE_TOLERANCE_PX) {
      p.moved = true;
      cancelLongPress();
    }

    // Two-finger pinch = zoom around the FINGER MIDPOINT (same behaviour as
    // the desktop mouse wheel with zoom-to-cursor). `zoomAt` keeps the world
    // point under the anchor at the same screen anchor across the scale
    // change, so content under the pinch stays under the pinch. Pan is a
    // separate gesture (single-finger drag) so we intentionally do NOT add
    // a midpoint-delta pan here — the fingers naturally drift downward as
    // they squeeze, and combining that with the zoom is what made the model
    // march off-screen in the earlier revision.
    if (state.pinch && state.pointers.size >= 2) {
      const [a, b] = Array.from(state.pointers.values());
      const dist = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      const midX = (a.x + b.x) / 2;
      const midY = (a.y + b.y) / 2;
      const scale = dist / state.pinch.lastDist;
      if (Math.abs(scale - 1) > 0.005) {
        const rect = canvas.getBoundingClientRect();
        zoomAt(midX - rect.left, midY - rect.top, scale);
      }
      state.pinch.lastDist = dist;
      state.pinch.lastMidX = midX;
      state.pinch.lastMidY = midY;
      return;
    }

    // Single-finger drag: rectangle-select (armed) or pan (default).
    if (state.pointers.size === 1 && state.isSelecting) {
      const local = canvasLocal(e);
      const x = Math.min(state.dragStartX, local.sx);
      const y = Math.min(state.dragStartY, local.sy);
      const w = Math.abs(local.sx - state.dragStartX);
      const h = Math.abs(local.sy - state.dragStartY);
      const isLeftToRight = local.sx > state.dragStartX;
      const mode: SelectionMode = isLeftToRight ? "inside" : "cross";
      state.selectMode = mode;
      setBox(x, y, w, h, mode);
      showMode(mode);
      return;
    }

    if (state.pointers.size === 1 && p.moved) {
      if (!state.isPanning) {
        state.isPanning = true;
        state.lastX = p.downX;
        state.lastY = p.downY;
        state.pendingClick = null;
      }
      const panDx = e.clientX - state.lastX;
      const panDy = e.clientY - state.lastY;
      state.lastX = e.clientX;
      state.lastY = e.clientY;
      panBy(panDx, panDy);
    }
  };

  const onTouchPointerUp = (e: PointerEvent) => {
    const p = state.pointers.get(e.pointerId);
    if (canvas.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }
    if (!p) return;
    state.pointers.delete(e.pointerId);

    // Coming down from 2 fingers to 1: end pinch. If the two fingers were
    // both tapped without movement, arm box-select for the next drag.
    if (state.pinch) {
      const bothStill = !p.moved && Array.from(state.pointers.values()).every((q) => !q.moved);
      const quick = performance.now() - p.downTime < 250;
      state.pinch = null;
      if (bothStill && quick) state.boxSelectArmed = true;
      // The remaining finger (if any) is a fresh reference frame for pan.
      const remaining = state.pointers.values().next().value as ActivePointer | undefined;
      if (remaining) {
        state.lastX = remaining.x;
        state.lastY = remaining.y;
      }
      return;
    }

    // Coming down from 1 finger to 0.
    if (state.pointers.size === 0) {
      cancelLongPress();
      if (state.isSelecting) {
        const local = canvasLocal(e);
        commitBoxSelection(local, false, false);
        state.isSelecting = false;
        hideBox();
        return;
      }
      if (state.isPanning) {
        state.isPanning = false;
        return;
      }
      if (state.pendingClick && !p.moved) {
        // Tap: commit the pick using the modifier state captured at down
        // (touch has none — always "replace" semantics).
        const pending = state.pendingClick;
        state.pendingClick = null;
        commitPickSelection(pending);
      } else {
        state.pendingClick = null;
      }
    }
  };

  const onTouchPointerCancel = (e: PointerEvent) => {
    if (canvas.hasPointerCapture(e.pointerId)) {
      canvas.releasePointerCapture(e.pointerId);
    }
    state.pointers.delete(e.pointerId);
    if (state.pointers.size === 0) {
      cancelLongPress();
      state.pendingClick = null;
      state.isPanning = false;
      state.isSelecting = false;
      state.pinch = null;
      state.boxSelectArmed = false;
      hideBox();
    }
  };

  // ---------------- MAIN DISPATCH ----------------

  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType === "mouse") onMousePointerDown(e);
    else onTouchPointerDown(e);
  };
  const onPointerMove = (e: PointerEvent) => {
    if (e.pointerType === "mouse") onMousePointerMove(e);
    else onTouchPointerMove(e);
  };
  const onPointerUp = (e: PointerEvent) => {
    if (e.pointerType === "mouse") onMousePointerUp(e);
    else onTouchPointerUp(e);
  };
  const onPointerCancel = (e: PointerEvent) => {
    if (e.pointerType === "mouse") {
      // Mouse pointercancel is rare; treat like leaving the canvas.
      state.isPanning = false;
      state.isSelecting = false;
      state.pendingClick = null;
      state.rightClickStart = null;
      if (state.rightClickTimer) {
        clearTimeout(state.rightClickTimer);
        state.rightClickTimer = 0;
      }
      hideBox();
      canvas.style.cursor = "crosshair";
    } else {
      onTouchPointerCancel(e);
    }
  };
  const onPointerLeave = (e: PointerEvent) => {
    // Only fire the mouse-idle reset when the mouse leaves the canvas.
    // Touch pointers with capture don't leave — pointerup / pointercancel
    // handle their teardown.
    if (e.pointerType !== "mouse") return;
    if (!state.isPanning && !state.isSelecting && !state.pendingClick) {
      canvas.style.cursor = "crosshair";
    }
  };

  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const local = canvasLocal(e);
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    zoomAt(local.sx, local.sy, factor);
  };

  const onContextMenu = (e: Event) => e.preventDefault();

  const onKeyDown = (e: KeyboardEvent) => {
    const active = document.activeElement;
    if (active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA")) return;

    const ctrl = e.ctrlKey || e.metaKey;
    if (ctrl && e.key === "o") {
      e.preventDefault();
      openFilePicker(loadDxfFromFile);
    } else if (ctrl && e.key === "s") {
      e.preventDefault();
      openModal("export", {});
    } else if (ctrl && e.key === "a") {
      e.preventDefault();
      selectAll();
    } else if (ctrl && e.key === "l") {
      e.preventDefault();
      toggleSidebar();
    } else if (ctrl && e.key === "r") {
      e.preventDefault();
      resetView();
    } else if (ctrl && e.key === "z") {
      e.preventDefault();
      undo();
    } else if (ctrl && e.key === "y") {
      e.preventDefault();
      redo();
    } else if (e.key === "Escape") {
      deselectAll();
      closeContextMenu();
    }
  };

  // touch-action:none tells the browser to route ALL touch gestures on the
  // canvas to us (including pinch-zoom, which would otherwise fire the page's
  // native zoom on mobile). Set on the DOM element rather than through CSS
  // so it applies regardless of any host stylesheet.
  const prevTouchAction = canvas.style.touchAction;
  canvas.style.touchAction = "none";

  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", onPointerUp);
  canvas.addEventListener("pointercancel", onPointerCancel);
  canvas.addEventListener("pointerleave", onPointerLeave);
  canvas.addEventListener("wheel", onWheel, { passive: false });
  canvas.addEventListener("contextmenu", onContextMenu);
  document.addEventListener("keydown", onKeyDown);

  // Chord-length helper: silence unused-var lint on TOUCH_DRAG_THRESHOLD_PX
  // in the extreme case someone yanks the pointerType branch. It's used
  // conceptually as the per-touch drag threshold — the actual check lives
  // inline in onTouchPointerMove via LONG_PRESS_MOVE_TOLERANCE_PX.
  void TOUCH_DRAG_THRESHOLD_PX;

  return () => {
    canvas.style.touchAction = prevTouchAction;
    canvas.removeEventListener("pointerdown", onPointerDown);
    canvas.removeEventListener("pointermove", onPointerMove);
    canvas.removeEventListener("pointerup", onPointerUp);
    canvas.removeEventListener("pointercancel", onPointerCancel);
    canvas.removeEventListener("pointerleave", onPointerLeave);
    canvas.removeEventListener("wheel", onWheel);
    canvas.removeEventListener("contextmenu", onContextMenu);
    document.removeEventListener("keydown", onKeyDown);
  };
}

function hitInput(canvas: HTMLCanvasElement) {
  const rect = canvas.getBoundingClientRect();
  return {
    entities: store.entities(),
    layers: store.layers(),
    layerOrder: store.layerOrder(),
    zoom: store.zoom(),
    pan: store.pan(),
    height: rect.height,
  };
}
